import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Copy, ExternalLink, Eye, Minus, Plus, RefreshCw, Sparkles, Trash2, TriangleAlert, CircleHelp, FileText, Send, PenLine, Mail } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { fmtNOK, fmtPct, fmtDate, fmtCompact } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { ProductImage } from "@/components/common";
import { ProposalDocument, unitPrice, groupTotal, isServiceGroup } from "@/components/proposal/ProposalDocument";

/* ------------------------------------------------------------------ frame
   One white sheet per step. Fixed header (what + key numbers), flexible body
   (only long lists scroll, with masked edges), fixed footer (one next action).
   The page around it never scrolls. */

export function Sheet({ eyebrow, title, meta, actions, footer, children, className, testId }) {
  const hasHeader = eyebrow || title || meta || actions;
  return (
    <section className={cn("sheet-shadow grid h-full min-h-0 grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden rounded-[22px] border border-black/[0.07] bg-white", className)} data-testid={testId}>
      {hasHeader ? (
        <header className="flex min-w-0 flex-wrap items-end justify-between gap-x-8 gap-y-3 px-6 pb-4 pt-5 sm:px-8">
          <div className="min-w-0 flex-1 basis-[260px]">
            {eyebrow ? <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{eyebrow}</div> : null}
            {title ? <h2 className="font-display mt-1.5 truncate text-[19px] font-semibold leading-tight tracking-[-0.02em] sm:text-[21px]" data-testid="workspace-sheet-title">{title}</h2> : null}
          </div>
          {meta ? <div className="flex min-w-0 max-w-full flex-wrap items-end gap-x-7 gap-y-2">{meta}</div> : null}
          {actions ? <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">{actions}</div> : null}
        </header>
      ) : (
        <div />
      )}
      <div className="min-h-0 min-w-0">{children}</div>
      {footer ? <footer className="flex min-w-0 flex-wrap items-center justify-between gap-3 border-t border-black/[0.06] px-6 py-3 sm:px-8">{footer}</footer> : <div />}
    </section>
  );
}

function Dot() {
  return <span className="h-[3px] w-[3px] rounded-full bg-black/25" aria-hidden />;
}

function Stat({ label, value, unit, tone, testId }) {
  return (
    <div className="text-right" data-testid={testId}>
      <div className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
      <div className={cn("font-display mt-1 whitespace-nowrap text-[18px] font-semibold tabular leading-none tracking-[-0.02em]", tone === "warn" && "text-[hsl(var(--status-warn))]", tone === "ok" && "text-[hsl(var(--status-ok))]")}>
        {value}
        {unit ? <span className="ml-1 text-[11px] font-medium tracking-normal text-muted-foreground">{unit}</span> : null}
      </div>
    </div>
  );
}

function Empty({ icon: Icon, title, text, action, testId }) {
  return (
    <div className="flex h-full flex-col items-center justify-center px-6 text-center" data-testid={testId}>
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(40_12%_95%)] text-muted-foreground"><Icon className="h-5 w-5" strokeWidth={1.8} /></span>
      <div className="font-display mt-5 text-[18px] font-semibold tracking-[-0.015em]">{title}</div>
      <p className="mt-1.5 max-w-sm text-[13.5px] leading-6 text-muted-foreground">{text}</p>
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

function StickyLabel({ children, right }) {
  return (
    <div className="sticky top-0 z-[1] flex items-baseline justify-between gap-3 bg-white/95 pb-1.5 pt-1 backdrop-blur-sm">
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{children}</div>
      {right}
    </div>
  );
}

const PRIORITY = { must: "Must", should: "Should", nice: "Nice" };

/* ------------------------------------------------------------------ understand */

export function UnderstandPanel({ ctx, act, busy, onAsk }) {
  const o = ctx.opportunity;
  const a = ctx.account || {};
  const reqs = ctx.requirements;
  const byCat = useMemo(() => {
    const m = new Map();
    (reqs || []).forEach((r) => m.set(r.category || "Other", [...(m.get(r.category || "Other") || []), r]));
    return [...m.entries()];
  }, [reqs]);
  const list = reqs || [];
  const open = (o.open_questions || []).slice(0, 8);
  const unclear = list.filter((r) => r.status === "needs_clarification").length;
  const musts = list.filter((r) => r.priority === "must").length;
  const facts = [
    ["Budget", o.budget],
    ["Deadline", o.deadline],
    ["Industry", a.industry],
    ["Location", a.city],
    ["Est. value", o.value ? fmtCompact(o.value) : null],
  ].filter(([, v]) => v);

  return (
    <Sheet
      eyebrow={
        <>
          <span>Understand</span>
          <Dot />
          <span data-testid="workspace-requirements-count">{list.length} requirements</span>
          {unclear ? (
            <>
              <Dot />
              <span className="text-[hsl(var(--status-warn))]">{unclear} need clarification</span>
            </>
          ) : null}
        </>
      }
      title="What the customer needs"
      meta={
        <>
          <Stat label="Must-haves" value={musts} testId="workspace-musts-stat" />
          <Stat label="Open" value={open.length} tone={open.length ? "warn" : "ok"} testId="workspace-open-stat" />
        </>
      }
      footer={
        <>
          <p className="text-[12.5px] text-muted-foreground">Answer open questions in the chat, or let me make sensible assumptions.</p>
          <Button variant="ink" className="rounded-full" onClick={() => act("generate_solution")} disabled={busy || list.length === 0} data-testid="workspace-generate-solution-button">
            <Sparkles className="text-[hsl(10_80%_72%)]" /> Design the solution
          </Button>
        </>
      }
      testId="workspace-understand"
    >
      <div className="grid h-full min-h-0 grid-cols-1 gap-x-10 px-6 sm:px-8 xl:grid-cols-[minmax(0,1fr)_300px]">
        <div className="flex min-h-0 flex-col">
          {o.summary ? <p className="mb-3 line-clamp-2 text-[14px] leading-6 text-foreground/80" data-testid="workspace-summary">{o.summary}</p> : null}
          {open.length ? (
            <div className="no-scrollbar mb-2 flex gap-1.5 overflow-x-auto pb-1 xl:hidden" data-testid="workspace-open-questions-strip">
              {open.map((q, i) => (
                <button key={i} type="button" onClick={() => onAsk?.(q)} className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-black/[0.08] bg-white px-3 text-[12.5px] hover:border-black/20 focus-visible:ring-2 focus-visible:ring-ring/40">
                  <CircleHelp className="h-3.5 w-3.5 text-[hsl(var(--status-warn))]" />
                  <span className="max-w-[240px] truncate">{q}</span>
                </button>
              ))}
            </div>
          ) : null}
          {list.length === 0 ? (
            <Empty icon={FileText} title="No requirements yet" text="Paste the inquiry or your notes in the chat and I'll extract them." testId="workspace-requirements-empty" />
          ) : (
            <div className="scroll-fade-b no-scrollbar min-h-0 flex-1 overflow-y-auto pb-8" data-testid="workspace-requirements">
              {byCat.map(([cat, list]) => (
                <div key={cat} className="mb-3 last:mb-0">
                  <StickyLabel right={<span className="font-mono text-[11px] tabular text-muted-foreground/70">{list.length}</span>}>{cat}</StickyLabel>
                  <ul className="divide-y divide-black/[0.05]">
                    {list.map((r) => (
                      <li key={r.id} className="flex items-start gap-3 py-2.5" data-testid="workspace-requirement">
                        <span className={cn("mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full", r.status === "resolved" ? "bg-[hsl(var(--status-ok))]" : r.status === "needs_clarification" ? "bg-[hsl(var(--status-warn))]" : "bg-black/[0.18]")} title={r.status} />
                        <div className="min-w-0 flex-1">
                          <div className="text-[13.5px] font-medium leading-snug">{r.title}</div>
                          {r.detail ? <div className="mt-0.5 line-clamp-1 text-[12.5px] leading-5 text-muted-foreground">{r.detail}</div> : null}
                        </div>
                        <span className={cn("shrink-0 pt-0.5 text-[10.5px] font-semibold uppercase tracking-[0.1em]", r.priority === "must" ? "text-foreground" : "text-muted-foreground/60")}>{PRIORITY[r.priority] || r.priority}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>

        <aside className="hidden min-h-0 flex-col xl:flex">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Open questions</div>
          {open.length === 0 ? (
            <p className="mt-2 text-[13px] text-muted-foreground">Nothing blocks the design.</p>
          ) : (
            <ul className="scroll-fade-b no-scrollbar -mx-2 mt-1.5 min-h-0 flex-1 overflow-y-auto pb-6" data-testid="workspace-open-questions">
              {open.map((q, i) => (
                <li key={i}>
                  <button type="button" onClick={() => onAsk?.(q)} className="group flex w-full items-start gap-2 rounded-[10px] px-2 py-1.5 text-left text-[13px] leading-5 hover:bg-black/[0.035] focus-visible:ring-2 focus-visible:ring-ring/40" data-testid="workspace-open-question">
                    <CircleHelp className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--status-warn))]" />
                    <span className="flex-1">{q}</span>
                    <PenLine className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {facts.length ? (
            <dl className="mt-auto grid grid-cols-2 gap-x-4 gap-y-3 border-t border-black/[0.06] pb-5 pt-4" data-testid="workspace-facts">
              {facts.map(([k, v]) => (
                <div key={k} className="min-w-0">
                  <dt className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{k}</dt>
                  <dd className="mt-0.5 truncate text-[13px] font-medium">{v}</dd>
                </div>
              ))}
            </dl>
          ) : null}
        </aside>
      </div>
    </Sheet>
  );
}

/* ------------------------------------------------------------------ design */

function Qty({ item, onChange, disabled }) {
  const q = item.qty || 0;
  return (
    <div className="inline-flex h-8 items-center rounded-full border border-black/[0.09] bg-white" data-testid="workspace-item-qty">
      <button type="button" disabled={disabled || q <= 1} onClick={() => onChange(q - 1)} className="flex h-8 w-8 items-center justify-center rounded-l-full text-muted-foreground hover:bg-black/[0.04] hover:text-foreground disabled:opacity-30 focus-visible:ring-2 focus-visible:ring-ring/40" aria-label="Decrease quantity" data-testid="workspace-item-qty-decrease">
        <Minus className="h-3.5 w-3.5" />
      </button>
      <span className="min-w-[30px] text-center font-mono text-[12.5px] tabular" data-testid="workspace-item-qty-value">
        {q}
        {item.unit && item.unit !== "pcs" ? <span className="text-muted-foreground"> {item.unit}</span> : ""}
      </span>
      <button type="button" disabled={disabled} onClick={() => onChange(q + 1)} className="flex h-8 w-8 items-center justify-center rounded-r-full text-muted-foreground hover:bg-black/[0.04] hover:text-foreground disabled:opacity-30 focus-visible:ring-2 focus-visible:ring-ring/40" aria-label="Increase quantity" data-testid="workspace-item-qty-increase">
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export function DesignPanel({ ctx, act, busy }) {
  const sol = ctx.solution;
  const t = sol?.totals || {};
  const groups = (sol?.groups || []).filter((g) => (g.items || []).length);

  if (!groups.length) {
    return (
      <Sheet eyebrow={<span>Design</span>} title="Solution" testId="workspace-design-empty">
        <Empty
          icon={Sparkles}
          title="No solution yet"
          text="I'll design one from the requirements - only products from your catalog, grouped and explained."
          action={
            <Button variant="ink" onClick={() => act("generate_solution")} disabled={busy} className="rounded-full" data-testid="workspace-generate-solution-button">
              <Sparkles className="text-[hsl(10_80%_72%)]" /> Design the solution
            </Button>
          }
        />
      </Sheet>
    );
  }

  const warnings = (sol.warnings || []).slice(0, 3);

  return (
    <Sheet
      eyebrow={
        <>
          <span>Design</span>
          <Dot />
          <span className="normal-case tracking-normal">{sol.name}</span>
          {sol.source === "ai" ? (
            <>
              <Dot />
              <span className="inline-flex items-center gap-1 normal-case tracking-normal"><Sparkles className="h-3 w-3 text-primary" /> designed by AI</span>
            </>
          ) : null}
        </>
      }
      title={sol.headline || sol.name}
      meta={
        <>
          <Stat label="Total" value={fmtNOK(t.total_sales)} unit="NOK" testId="workspace-design-total" />
          <Stat label="Margin" value={fmtPct(t.gm_pct)} tone={(t.gm_pct || 0) < 30 ? "warn" : undefined} testId="workspace-design-gm" />
          <Stat label="Lead time" value={t.max_lead_time_weeks ? `${t.max_lead_time_weeks}` : "–"} unit={t.max_lead_time_weeks ? "wks" : undefined} testId="workspace-design-lead" />
        </>
      }
      footer={
        <>
          <div className="flex min-w-0 items-center gap-3">
            <Button variant="ghost" className="rounded-full" onClick={() => act("regenerate_solution", { variant_hint: "" })} disabled={busy} data-testid="workspace-regenerate-solution-button">
              <RefreshCw className="h-3.5 w-3.5" /> Regenerate
            </Button>
            <span className="hidden truncate text-[12.5px] text-muted-foreground md:inline">Ask in the chat to swap, add or remove products.</span>
          </div>
          <Button variant="ink" className="rounded-full" onClick={() => act("go_to_step", { step: "calculate" })} disabled={busy} data-testid="workspace-continue-calculate-button">
            Continue to calculation <ArrowRight />
          </Button>
        </>
      }
      testId="workspace-design"
    >
      <div className="flex h-full min-h-0 flex-col px-6 sm:px-8">
        {sol.rationale || warnings.length ? (
          <div className="mb-2 flex flex-wrap items-start justify-between gap-x-8 gap-y-2">
            {sol.rationale ? <p className="line-clamp-2 min-w-0 flex-1 basis-[320px] text-[13.5px] leading-6 text-muted-foreground" data-testid="workspace-solution-rationale">{sol.rationale}</p> : null}
            {warnings.length ? (
              <ul className="flex flex-wrap gap-1.5" data-testid="workspace-solution-warnings">
                {warnings.map((w, i) => (
                  <li key={i} className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-[hsl(var(--status-warn)/0.1)] px-2.5 py-1 text-[12px] text-foreground/85 sm:max-w-[360px]">
                    <TriangleAlert className="h-3 w-3 shrink-0 text-[hsl(var(--status-warn))]" />
                    <span className="truncate">{w.message || w}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
        <div className="scroll-fade-b no-scrollbar min-h-0 flex-1 overflow-y-auto pb-8" data-testid="workspace-solution-groups">
          {groups.map((g) => {
            const services = isServiceGroup(g);
            return (
              <div key={g.id} className="mb-4 last:mb-0" data-testid="workspace-solution-group">
                <StickyLabel right={<span className="font-mono text-[12px] tabular text-muted-foreground">{fmtNOK(groupTotal(g))}</span>}>
                  <span className="text-foreground">{g.name}</span>
                  <span className="ml-2 font-mono text-[10.5px] tracking-normal text-muted-foreground/70">{g.items.length}</span>
                </StickyLabel>
                <ul className="divide-y divide-black/[0.05]">
                  {g.items.map((it) => (
                    <li key={it.id} className="group/item flex items-center gap-3.5 py-2.5" data-testid="workspace-solution-item">
                      {services ? (
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-[hsl(40_12%_96%)] font-mono text-[9.5px] text-muted-foreground">HRS</span>
                      ) : (
                        <ProductImage item={it} size="h-10 w-10" fit="auto" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2">
                          <span className="truncate text-[13.5px] font-medium leading-snug">{it.name}</span>
                          {!services && it.manufacturer ? <span className="hidden shrink-0 text-[11.5px] text-muted-foreground sm:inline">{it.manufacturer}</span> : null}
                        </div>
                        {it.reason ? <div className="line-clamp-1 text-[12.5px] leading-5 text-muted-foreground">{it.reason}</div> : null}
                      </div>
                      <Qty item={it} disabled={busy} onChange={(q) => act("update_item", { item_id: it.id, qty: q })} />
                      <span className="hidden w-24 text-right font-mono text-[12.5px] tabular sm:block">{fmtNOK(unitPrice(it) * it.qty)}</span>
                      <button type="button" disabled={busy} onClick={() => act("remove_item", { item_id: it.id })} className="hidden h-8 w-8 items-center justify-center rounded-full text-muted-foreground opacity-40 transition-opacity duration-150 hover:bg-black/[0.05] hover:text-[hsl(var(--status-bad))] group-hover/item:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring/40 disabled:opacity-20 sm:flex" aria-label={`Remove ${it.name}`} data-testid="workspace-item-remove-button">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </Sheet>
  );
}

/* ------------------------------------------------------------------ calculate */

function DiscountInput({ value, onCommit, disabled }) {
  const [v, setV] = useState(value ?? 0);
  useEffect(() => setV(value ?? 0), [value]);
  return (
    <span className="inline-flex h-8 items-center rounded-full border border-black/[0.09] bg-white pl-2.5 pr-1 font-mono text-[12.5px] tabular focus-within:border-black/[0.25]">
      <input
        type="number"
        min={0}
        max={60}
        value={v}
        disabled={disabled}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => Number(v) !== Number(value ?? 0) && onCommit(Number(v) || 0)}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        className="w-10 bg-transparent text-right outline-none disabled:opacity-50"
        aria-label="Discount percent"
        data-testid="workspace-group-discount-input"
      />
      <span className="pr-1.5 text-muted-foreground">%</span>
    </span>
  );
}

export function CalculatePanel({ ctx, act, busy }) {
  const sol = ctx.solution;
  if (!sol) return <DesignPanel ctx={ctx} act={act} busy={busy} />;
  const t = sol.totals || {};
  const groups = (sol.groups || []).filter((g) => (g.items || []).length);
  const approval = t.approval || {};
  const buckets = [
    ["Hardware", t.hardware || 0, "bg-[hsl(var(--ink))]"],
    ["Software", t.software || 0, "bg-primary"],
    ["Services", t.services || 0, "bg-[hsl(200_30%_60%)]"],
  ];
  const total = t.total_sales || 1;
  const lowGm = (t.gm_pct || 0) < 30;

  return (
    <Sheet
      eyebrow={
        <>
          <span>Calculate</span>
          <Dot />
          <span className="normal-case tracking-normal">{sol.name}</span>
        </>
      }
      title="Pricing and margin"
      actions={
        approval.required ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--status-warn)/0.1)] px-3 py-1.5 text-[12.5px] font-medium text-foreground" data-testid="workspace-approval-chip" title={approval.reason || ""}>
            <TriangleAlert className="h-3.5 w-3.5 text-[hsl(var(--status-warn))]" /> {approval.label || "Approval required"}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--status-ok)/0.1)] px-3 py-1.5 text-[12.5px] font-medium text-foreground" data-testid="workspace-approval-chip">
            <Check className="h-3.5 w-3.5 text-[hsl(var(--status-ok))]" strokeWidth={3} /> Within your mandate
          </span>
        )
      }
      footer={
        <>
          <p className="text-[12.5px] text-muted-foreground">Change a discount and I recalculate instantly - totals are always deterministic.</p>
          <Button variant="ink" className="rounded-full" onClick={() => act("generate_quote")} disabled={busy} data-testid="workspace-generate-quote-button">
            <Sparkles className="text-[hsl(10_80%_72%)]" /> Write the proposal
          </Button>
        </>
      }
      testId="workspace-calculate"
    >
      <div className="flex h-full min-h-0 flex-col px-6 sm:px-8">
        <div className="grid grid-cols-2 gap-x-8 gap-y-4 border-b border-black/[0.06] pb-4 md:grid-cols-4" data-testid="workspace-calc-kpis">
          {[
            ["Total excl. VAT", fmtNOK(t.total_sales), "NOK", false, "workspace-calc-total"],
            ["Cost", fmtNOK(t.total_cost), "NOK", false, "workspace-calc-cost"],
            ["Gross margin", fmtPct(t.gm_pct), `${fmtNOK(t.gross_margin)} NOK`, lowGm, "workspace-calc-gm"],
            ["Lead time", t.max_lead_time_weeks ? `${t.max_lead_time_weeks} wks` : "–", "longest supplier", false, "workspace-calc-lead"],
          ].map(([l, v, s, warn, tid]) => (
            <div key={l} data-testid={tid}>
              <div className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{l}</div>
              <div className={cn("font-display mt-1.5 whitespace-nowrap text-[22px] font-semibold tabular leading-none tracking-[-0.03em]", warn && "text-[hsl(var(--status-warn))]")}>{v}</div>
              <div className="mt-1 truncate text-[12px] text-muted-foreground">{s}</div>
            </div>
          ))}
        </div>
        <div className="py-3.5" data-testid="workspace-calc-mix">
          <div className="flex h-1.5 overflow-hidden rounded-full bg-black/[0.05]">
            {buckets.map(([l, v, c]) => (
              <span key={l} className={cn("h-full", c)} style={{ width: `${(v / total) * 100}%` }} title={l} />
            ))}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[12px]">
            {buckets.map(([l, v, c]) => (
              <span key={l} className="inline-flex items-center gap-1.5">
                <span className={cn("h-1.5 w-1.5 rounded-full", c)} />
                {l} <span className="font-mono tabular text-muted-foreground">{fmtNOK(v)}</span>
              </span>
            ))}
          </div>
        </div>
        <div className="scroll-fade-b no-scrollbar min-h-0 flex-1 overflow-y-auto pb-8" data-testid="workspace-calc-groups">
          <table className="w-full text-[13.5px]">
            <thead className="sticky top-0 z-[1] bg-white/95 backdrop-blur-sm">
              <tr className="text-[10.5px] uppercase tracking-[0.12em] text-muted-foreground">
                <th className="pb-2 pt-1 text-left font-semibold">Group</th>
                <th className="hidden pb-2 pt-1 text-right font-semibold sm:table-cell">Lines</th>
                <th className="hidden pb-2 pt-1 text-right font-semibold md:table-cell">Cost</th>
                <th className="pb-2 pt-1 text-right font-semibold">Sales</th>
                <th className="pb-2 pt-1 text-right font-semibold">GM</th>
                <th className="pb-2 pt-1 text-right font-semibold">Discount</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => {
                const sales = groupTotal(g);
                const c = g.items.reduce((acc, it) => acc + (it.cost_price || 0) * (it.qty || 0), 0);
                const gm = sales ? ((sales - c) / sales) * 100 : 0;
                const services = isServiceGroup(g);
                const disc = g.items.find((it) => !it.is_service)?.discount_pct || 0;
                return (
                  <tr key={g.id} className="border-t border-black/[0.05]" data-testid="workspace-calc-group-row">
                    <td className="py-2 font-medium">{g.name}</td>
                    <td className="hidden py-2 text-right font-mono tabular text-muted-foreground sm:table-cell">{g.items.length}</td>
                    <td className="hidden py-2 text-right font-mono tabular text-muted-foreground md:table-cell">{fmtNOK(c)}</td>
                    <td className="py-2 text-right font-mono tabular font-medium">{fmtNOK(sales)}</td>
                    <td className={cn("py-2 text-right font-mono tabular", gm < 25 && "text-[hsl(var(--status-warn))]")}>{fmtPct(gm)}</td>
                    <td className="py-2 text-right">{services ? <span className="text-muted-foreground">–</span> : <DiscountInput value={disc} disabled={busy} onCommit={(pct) => act("set_group_discount", { group_name: g.name, discount_pct: pct })} />}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Sheet>
  );
}

/* ------------------------------------------------------------------ propose */

const TONE_CHIPS = ["Shorten the executive summary", "Make the summary more technical", "Warmer, more personal tone", "Add a benefit about local support"];

export function ProposePanel({ ctx, act, busy, onSay }) {
  const q = ctx.quote;
  if (!q) {
    return (
      <Sheet eyebrow={<span>Propose</span>} title="Proposal" testId="workspace-propose-empty">
        <Empty
          icon={FileText}
          title="No proposal yet"
          text="I'll write a customer-ready proposal from the active solution: summary, understanding, solution, investment, timeline and terms."
          action={
            <Button variant="ink" onClick={() => act("generate_quote")} disabled={busy || !ctx.solution} className="rounded-full" data-testid="workspace-generate-quote-button">
              <Sparkles className="text-[hsl(10_80%_72%)]" /> Write the proposal
            </Button>
          }
        />
      </Sheet>
    );
  }
  const data = { quote: q, opportunity: { name: ctx.opportunity?.name, summary: ctx.opportunity?.summary }, account: ctx.account, owner: ctx.owner, contacts: ctx.contacts, company: ctx.company };
  const link = `${window.location.origin}/q/${q.token}`;
  return (
    <Sheet
      eyebrow={
        <>
          <span>Propose</span>
          <Dot />
          <span data-testid="workspace-quote-version">v{q.version}</span>
          <Dot />
          <span data-testid="workspace-quote-status">{q.status}</span>
          <Dot />
          <span className="normal-case tracking-normal">Valid until {fmtDate(q.valid_until, { year: true })}</span>
        </>
      }
      title={q.title}
      actions={
        <>
          <Button asChild variant="ghost" className="rounded-full">
            <a href={link} target="_blank" rel="noreferrer" data-testid="workspace-open-proposal-link">
              Open as customer <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => act("generate_quote")} disabled={busy} data-testid="workspace-regenerate-quote-button">
            <RefreshCw className="h-3.5 w-3.5" /> New version
          </Button>
          {q.status === "draft" ? (
            <Button variant="ink" className="rounded-full" onClick={() => act("send_quote", { origin: window.location.origin })} disabled={busy} data-testid="workspace-send-quote-button">
              <Send className="h-3.5 w-3.5" /> Send proposal
            </Button>
          ) : null}
        </>
      }
      footer={
        <div className="no-scrollbar flex w-full items-center gap-1.5 overflow-x-auto" data-testid="workspace-tone-chips">
          <span className="mr-1 shrink-0 text-[12px] text-muted-foreground">Refine</span>
          {TONE_CHIPS.map((c) => (
            <button key={c} type="button" disabled={busy} onClick={() => onSay?.(c)} className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-black/[0.09] bg-white px-3 text-[12.5px] font-medium text-foreground/85 transition-[border-color] duration-150 hover:border-black/[0.22] disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-ring/40" data-testid="workspace-tone-chip">
              <Sparkles className="h-3 w-3 text-primary" />
              {c}
            </button>
          ))}
        </div>
      }
      testId="workspace-propose"
    >
      <div className="scrollbar-thin h-full min-h-0 overflow-y-auto border-t border-black/[0.05] bg-[hsl(40_12%_97%)] px-2 py-3 sm:px-8 sm:py-5" data-testid="workspace-proposal-preview">
        <div className="mx-auto max-w-[920px] rounded-[16px] border border-black/[0.06] bg-white px-4 py-6 shadow-[0_1px_2px_hsl(30_12%_18%/0.04),0_24px_60px_-40px_hsl(30_12%_18%/0.4)] sm:px-12 sm:py-14">
          <ProposalDocument data={data} />
        </div>
      </div>
    </Sheet>
  );
}

/* ------------------------------------------------------------------ send */

export function SendPanel({ ctx, act, busy, session }) {
  const q = ctx.quote;
  if (!q || q.status === "draft") return <ProposePanel ctx={ctx} act={act} busy={busy} />;
  const link = `${window.location.origin}/q/${q.token}`;
  const draft = session?.email_draft;
  const summary = typeof q.sections?.executive_summary === "string" ? q.sections.executive_summary : null;
  const copy = (text, what) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${what} copied`);
  };
  return (
    <Sheet
      eyebrow={
        <>
          <span>Send</span>
          <Dot />
          <span>v{q.version}</span>
          <Dot />
          <span className="normal-case tracking-normal">{q.sent_at ? `Sent ${fmtDate(q.sent_at, { year: true })}` : "Sent"}</span>
        </>
      }
      title={
        <span className="inline-flex items-center gap-2.5">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--status-ok))] text-white"><Check className="h-3.5 w-3.5" strokeWidth={3} /></span>
          Proposal is live
        </span>
      }
      actions={
        <>
          <Button variant="outline" onClick={() => copy(link, "Link")} className="rounded-full" data-testid="workspace-copy-link-button">
            <Copy className="h-3.5 w-3.5" /> Copy link
          </Button>
          <Button asChild variant="ink" className="rounded-full">
            <a href={link} target="_blank" rel="noreferrer" data-testid="workspace-open-sent-link">
              Open <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </Button>
        </>
      }
      footer={
        <>
          <p className="text-[12.5px] text-muted-foreground">Follow up in 3-5 days if there's no response. When the customer accepts, mark the opportunity as Won.</p>
          <Button asChild variant="soft" className="rounded-full">
            <Link to={`/opportunities/${ctx.opportunity.id}`} data-testid="workspace-send-open-opportunity">
              Go to opportunity <ArrowRight />
            </Link>
          </Button>
        </>
      }
      testId="workspace-send"
    >
      <div className="grid h-full min-h-0 grid-cols-1 gap-x-10 px-6 sm:px-8 lg:grid-cols-[minmax(0,1fr)_260px]">
        <div className="flex min-h-0 flex-col">
          <div className="flex items-center gap-2 rounded-full border border-black/[0.07] bg-[hsl(40_12%_97.5%)] py-2 pl-4 pr-4" data-testid="workspace-sent-banner">
            <code className="min-w-0 flex-1 truncate font-mono text-[12.5px]" data-testid="workspace-proposal-link">{link}</code>
            <span className="shrink-0 text-[11.5px] text-muted-foreground">{q.views || 0} views</span>
          </div>

          <ol className="mt-4 grid grid-cols-1 gap-x-8 gap-y-2 border-b border-black/[0.06] pb-4 sm:grid-cols-3" data-testid="workspace-send-activity">
            {[
              [Send, "Sent", q.sent_at ? fmtDate(q.sent_at, { year: true }) : "–", true],
              [Eye, q.views ? `Viewed ${q.views} ${q.views === 1 ? "time" : "times"}` : "Not viewed yet", q.last_viewed_at ? `Last ${fmtDate(q.last_viewed_at, { year: true })}` : "We'll show it here", !!q.views],
              [Check, q.customer_accepted_at ? "Accepted" : q.customer_response ? "Changes requested" : "Awaiting response", q.customer_accepted_at ? `${q.customer_accepted_by || "Customer"} · ${fmtDate(q.customer_accepted_at, { year: true })}` : `Valid until ${fmtDate(q.valid_until, { year: true })}`, !!q.customer_accepted_at],
            ].map(([Icon, label, sub, done], i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className={cn("mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full", done ? "bg-[hsl(var(--status-ok)/0.12)] text-[hsl(var(--status-ok))]" : "bg-black/[0.05] text-muted-foreground")}>
                  <Icon className="h-3 w-3" strokeWidth={2.5} />
                </span>
                <span className="min-w-0">
                  <span className="block text-[13px] font-medium leading-5">{label}</span>
                  <span className="block truncate text-[12px] text-muted-foreground">{sub}</span>
                </span>
              </li>
            ))}
          </ol>

          {draft ? (
            <div className="mt-4 flex min-h-0 flex-1 flex-col" data-testid="workspace-send-email">
              <div className="flex items-center justify-between">
                <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground"><Mail className="h-3 w-3" /> Cover e-mail</div>
                <button type="button" onClick={() => copy(`Subject: ${draft.subject}\n\n${draft.body}`, "E-mail")} className="inline-flex h-7 items-center gap-1.5 rounded-full px-2 text-[12px] text-muted-foreground hover:bg-black/[0.05] hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40" data-testid="workspace-send-email-copy-button">
                  <Copy className="h-3 w-3" /> Copy
                </button>
              </div>
              {draft.to ? <div className="mt-2 text-[12.5px] text-muted-foreground">To: {draft.to}</div> : null}
              <div className="mt-0.5 text-[14px] font-semibold">{draft.subject}</div>
              <p className="scroll-fade-b no-scrollbar mt-2 min-h-0 flex-1 overflow-y-auto whitespace-pre-wrap pb-8 text-[13.5px] leading-6 text-foreground/85">{draft.body}</p>
            </div>
          ) : summary ? (
            <div className="mt-4 flex min-h-0 flex-1 flex-col" data-testid="workspace-send-summary-text">
              <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground"><FileText className="h-3 w-3" /> What the customer reads first</div>
              <p className="scroll-fade-b no-scrollbar mt-2 min-h-0 flex-1 overflow-y-auto whitespace-pre-wrap pb-8 text-[13.5px] leading-6 text-foreground/85">{summary}</p>
              <p className="pb-4 text-[12.5px] text-muted-foreground">Ask me in the chat to draft a follow-up e-mail.</p>
            </div>
          ) : (
            <p className="mt-4 text-[13.5px] leading-6 text-muted-foreground">Ask me in the chat to draft the cover e-mail or a follow-up.</p>
          )}
        </div>
        <aside className="hidden lg:block" data-testid="workspace-send-summary">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-4">
            <div>
              <dt className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Total</dt>
              <dd className="mt-0.5 font-mono text-[13.5px] tabular font-medium">{fmtNOK(q.totals?.total_sales)}</dd>
            </div>
            <div>
              <dt className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Valid until</dt>
              <dd className="mt-0.5 font-mono text-[13.5px] tabular font-medium">{fmtDate(q.valid_until, { year: true })}</dd>
            </div>
            <div>
              <dt className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Status</dt>
              <dd className="mt-0.5 text-[13.5px] font-medium capitalize">{q.status}</dd>
            </div>
            <div>
              <dt className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Version</dt>
              <dd className="mt-0.5 font-mono text-[13.5px] tabular font-medium">v{q.version}</dd>
            </div>
          </dl>
          <p className="mt-5 border-t border-black/[0.06] pt-4 text-[12.5px] leading-5 text-muted-foreground">The customer can read, download and accept online. Views and responses show up on the opportunity.</p>
        </aside>
      </div>
    </Sheet>
  );
}

/* ------------------------------------------------------------------ empty (no opportunity yet) */

export function EmptyCanvas({ onSay }) {
  return (
    <Sheet testId="workspace-empty-canvas">
      <div className="flex h-full items-center justify-center px-6">
        <div className="max-w-md text-center">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(40_12%_95%)]"><Sparkles className="h-5 w-5 text-primary" strokeWidth={1.8} /></span>
          <h2 className="font-display mt-5 text-[22px] font-semibold tracking-[-0.02em]">Your canvas fills up as we go</h2>
          <p className="mt-2 text-[14px] leading-6 text-muted-foreground">Requirements, the designed solution, pricing and the finished proposal appear here - always saved on the opportunity.</p>
          <button type="button" onClick={() => onSay?.("Use example inquiry")} className="mt-5 inline-flex h-9 items-center gap-2 rounded-full border border-black/[0.09] bg-white px-4 text-[13px] font-medium hover:border-black/[0.22] focus-visible:ring-2 focus-visible:ring-ring/40" data-testid="workspace-example-button">
            Try with an example inquiry <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </Sheet>
  );
}
